import { useCallback, useEffect, useRef } from "react";

interface UseStreamBufferOptions {
  /** Minimum characters per second — floor for drain rate (default: 30) */
  minCharsPerSecond?: number;
  /** Maximum characters per second — ceiling for drain rate (default: 400) */
  maxCharsPerSecond?: number;
  /** Target time window (ms) to fully drain the current buffer (default: 800) */
  drainWindowMs?: number;
  /** Drain window (ms) used once the stream has signalled completion (default: 250) */
  finalDrainWindowMs?: number;
}

/**
 * Buffers incoming stream tokens and releases them at an adaptive rate via
 * requestAnimationFrame, smoothing out bursty SSE delivery.
 *
 * Drain rate is proportional to the current buffer size: rate = bufferSize * 1000 / drainWindowMs.
 * This self-balances — a fast backend produces a large buffer which drains fast,
 * a slow backend produces a small buffer which drains slow. Rate is clamped to
 * [minCharsPerSecond, maxCharsPerSecond] for readability.
 *
 * Once `isFinalChunk` is received, the drain window shrinks so the remaining
 * buffered text visibly finishes instead of being dumped at completion time.
 *
 * @param onFlush Called with each batch of released characters. `isFinal` is
 *   true after all buffered text has been drained and the stream signalled
 *   completion.
 */
export function useStreamBuffer(onFlush: (text: string, isFinal: boolean) => void, options?: UseStreamBufferOptions) {
  const {
    minCharsPerSecond = 30,
    maxCharsPerSecond = 400,
    drainWindowMs = 800,
    finalDrainWindowMs = 250,
  } = options ?? {};

  const bufferRef = useRef("");
  const finalReceivedRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;

  const stopLoop = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastFrameTimeRef.current = null;
  }, []);

  const tick = useCallback(
    (now: number) => {
      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = now;
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const elapsed = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      const buffer = bufferRef.current;

      if (buffer.length === 0) {
        if (finalReceivedRef.current) {
          stopLoop();
          onFlushRef.current("", true);
          return;
        }
        // Buffer empty but stream not done — keep loop alive, wait for more
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      // Adaptive rate: aim to drain the current buffer within drainWindowMs.
      // Clamped so short bursts don't flash and long bursts don't outrun reading.
      const window = finalReceivedRef.current ? finalDrainWindowMs : drainWindowMs;
      let rate = (buffer.length * 1000) / window;
      if (rate < minCharsPerSecond) rate = minCharsPerSecond;
      if (rate > maxCharsPerSecond) rate = maxCharsPerSecond;

      let charsToRelease = Math.max(1, Math.floor((elapsed * rate) / 1000));
      charsToRelease = Math.min(charsToRelease, buffer.length);

      const released = buffer.slice(0, charsToRelease);
      bufferRef.current = buffer.slice(charsToRelease);

      onFlushRef.current(released, false);

      // If buffer just emptied and stream is done, flush final
      if (bufferRef.current.length === 0 && finalReceivedRef.current) {
        stopLoop();
        onFlushRef.current("", true);
        return;
      }

      rafIdRef.current = requestAnimationFrame(tick);
    },
    [drainWindowMs, finalDrainWindowMs, maxCharsPerSecond, minCharsPerSecond, stopLoop]
  );

  const startLoop = useCallback(() => {
    if (rafIdRef.current !== null) return; // already running
    lastFrameTimeRef.current = null;
    rafIdRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const pushChunk = useCallback(
    (text: string, isFinalChunk?: boolean) => {
      if (text) {
        bufferRef.current += text;
      }
      if (isFinalChunk) {
        finalReceivedRef.current = true;
      }
      startLoop();
    },
    [startLoop]
  );

  const flush = useCallback(() => {
    stopLoop();
    const remaining = bufferRef.current;
    bufferRef.current = "";
    finalReceivedRef.current = false;
    if (remaining) {
      onFlushRef.current(remaining, true);
    } else {
      onFlushRef.current("", true);
    }
  }, [stopLoop]);

  const reset = useCallback(() => {
    stopLoop();
    bufferRef.current = "";
    finalReceivedRef.current = false;
  }, [stopLoop]);

  // Cleanup on unmount
  useEffect(() => stopLoop, [stopLoop]);

  return { pushChunk, flush, reset };
}
